// Reads and writes one project's P&L view overlay, which lives in
// claims_project_config.config -> 'pnl_view'.
//
// The rules this hook exists to keep:
//
//   * ONE WRITE PER COMMIT. Every write to claims_project_config snapshots the
//     whole row into claims_project_config_history through a trigger
//     (20260904000100 section 5), which is how "who hid what, and when" is
//     audited for free. Saving per drag frame or per keystroke would flood that
//     table, so the editor calls saveView once, when the user commits.
//
//   * THE ROW COUNT, NOT THE ERROR. A Supabase update refused by RLS returns
//     neither rows nor an error. Treating that as success is the silent revert
//     this codebase has been bitten by repeatedly, so every write selects the
//     key back and treats zero rows as a refusal. Local state moves only after
//     a confirmed write, so the screen never shows a change the database did
//     not accept.
//
//   * THE CONFIG COLUMN IS SHARED. It holds other per project blocks, so a save
//     re-reads the row and merges, rather than writing back the copy this hook
//     loaded on mount.
//
// Who may write is decided by the database: claims_project_config's UPDATE
// policy requires organisation membership AND profiles.can_view_financials
// (20260915000200). This hook adds no second opinion, it only reports refusals
// honestly.
//
// claimTimingDb rather than the plain client for the same reason SEQ-169 used
// it: database.types.ts is generated from the live schema and cannot describe
// a migration that has not been applied yet.

import { useCallback, useEffect, useState } from "react";
import { claimTimingDb } from "../../lib/claimTimingDb";
import {
  inheritEverything,
  parseProjectPnlView,
  type ProjectPnlView,
  serialiseProjectPnlView,
} from "./projectPnlView";

export interface SaveProjectPnlViewResult {
  ok: boolean;
  /** Present when ok is false. Written for a person, not for a log. */
  message?: string;
}

export interface UseProjectPnlViewReturn {
  view: ProjectPnlView;
  loading: boolean;
  saving: boolean;
  /** Set when the overlay could not be read. The tab still renders, inheriting everything. */
  loadError: string | null;
  saveView: (next: ProjectPnlView) => Promise<SaveProjectPnlViewResult>;
  refresh: () => Promise<void>;
}

const REFUSED =
  "Nothing was saved. You may not have permission to change this project's P&L layout.";

interface FetchedView {
  view: ProjectPnlView;
  error: string | null;
}

/** The read itself, holding no state, so the mount and refresh share one path. */
async function fetchProjectPnlView(projectId: string): Promise<FetchedView> {
  const { data, error } = await claimTimingDb
    .from("claims_project_config")
    .select("config")
    .eq("project_id", projectId)
    .maybeSingle();

  // A project whose overlay cannot be read still renders its P&L, with every
  // organisation category on it, which is the state every project is in until
  // someone changes it.
  if (error) return { view: inheritEverything(), error: error.message };
  return { view: parseProjectPnlView(data?.config ?? null), error: null };
}

export function useProjectPnlView(projectId: string): UseProjectPnlViewReturn {
  const [view, setView] = useState<ProjectPnlView>(inheritEverything);
  // Derived from the argument rather than set inside the effect: a hook with
  // no project to read is not loading, and a synchronous setState in an effect
  // is a cascading render (react-hooks/set-state-in-effect).
  const [loading, setLoading] = useState(Boolean(projectId));
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    void (async () => {
      const result = await fetchProjectPnlView(projectId);
      if (cancelled) return;
      setView(result.view);
      setLoadError(result.error);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const refresh = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    const result = await fetchProjectPnlView(projectId);
    setView(result.view);
    setLoadError(result.error);
    setLoading(false);
  }, [projectId]);

  const saveView = useCallback(
    async (next: ProjectPnlView): Promise<SaveProjectPnlViewResult> => {
      if (!projectId) return { ok: false, message: "No project in context." };
      setSaving(true);

      // Re-read rather than reusing the copy loaded on mount: config carries
      // other per project blocks, and this write must not roll one of them back.
      const { data: current, error: readError } = await claimTimingDb
        .from("claims_project_config")
        .select("config")
        .eq("project_id", projectId)
        .maybeSingle();

      if (readError || !current) {
        setSaving(false);
        return {
          ok: false,
          message: readError?.message ?? "This project has no configuration row, so the layout could not be saved.",
        };
      }

      const merged = { ...(current.config ?? {}), pnl_view: serialiseProjectPnlView(next) };

      const { data, error } = await claimTimingDb
        .from("claims_project_config")
        .update({ config: merged })
        .eq("project_id", projectId)
        .select("project_id");

      setSaving(false);

      if (error) return { ok: false, message: error.message };
      // Row count as well as the error: an update refused by RLS returns
      // neither, and the database also refuses a malformed block outright
      // (20260915000100), so silence here is never proof of a write.
      if (!data || data.length === 0) return { ok: false, message: REFUSED };

      setView(next);
      return { ok: true };
    },
    [projectId],
  );

  return { view, loading, saving, loadError, saveView, refresh };
}
